import { useToast } from '@razorpay/blade/components';
import {
  ModalActions,
  salaryComponentParams,
  salaryComponentsQueries,
} from 'components/SalaryComponents/constants';
import { useModalActions } from 'components/SalaryComponents/hooks/useModalActions';
import { DisableModal, EnableModal } from 'components/SalaryComponents/modals/index';
import React from 'react';
import { useQueryClient } from 'react-query';
import { AppError } from 'utils/AppError';
import { useDetailsView } from '../hooks/useDetailsView';
import { SalaryComponent, EarningsComponentType } from '../types';

type ActionModalsProps = {
  salaryComponent: SalaryComponent;
  currentActiveModal: string | null;
  closeModal: () => void;
  enableModalContent: React.ReactNode;
  disableModalContent: React.ReactNode;
};

const ActionModals = ({
  salaryComponent,
  currentActiveModal,
  closeModal,
  enableModalContent,
  disableModalContent,
}: ActionModalsProps) => {
  const toast = useToast();
  const { closeDetailsView } = useDetailsView(salaryComponentParams.componentId);

  const onSuccess = () => {
    switch (currentActiveModal) {
      case ModalActions.enable:
        toast.show({
          id: salaryComponentId,
          content: 'Component enabled successfully',
          color: 'positive',
        });
        break;
      case ModalActions.disable:
        toast.show({
          id: salaryComponentId,
          content: 'Component disabled successfully',
          color: 'information',
        });
        break;
      case ModalActions.delete:
        toast.show({
          id: salaryComponentId,
          content: 'Component deleted successfully',
          color: 'negative',
        });
        break;
    }

    closeModal();
    queryClient.invalidateQueries({
      queryKey: [salaryComponentsQueries.getSalaryComponentDetailsQuery],
      exact: false,
    });
    // Force an immediate refetch
    queryClient.refetchQueries({
      queryKey: [salaryComponentsQueries.getSalaryComponentsList],
      exact: false,
    });

    closeDetailsView();
  };

  const onError = (error: AppError) => {
    toast.show({
      id: salaryComponentId,
      content: `There was an error changing status: ${error.message}`,
      color: 'negative',
    });
  };

  const { salaryComponentId, actionMutation } = useModalActions({
    modalAction: currentActiveModal,
    salaryComponent: {
      ...salaryComponent,
      category: salaryComponent.category || 'PERQUISITES_CATEGORY',
    } as SalaryComponent,
    onSuccess: onSuccess,
    onError: onError,
  });
  const queryClient = useQueryClient();
  const isOpen = !!salaryComponentId && !!currentActiveModal;
  const isLoading = actionMutation.isLoading;
  const onSubmit = () => {
    let payType = undefined;
    if (salaryComponent.category === 'EARNINGS_CATEGORY') {
      payType = (salaryComponent as EarningsComponentType).settings.earningDetails.payType;
    }
    actionMutation.mutate({
      id: salaryComponentId,
      category: salaryComponent.category,
      status:
        currentActiveModal === ModalActions.enable ? 'SET_STATUS_ACTIVE' : 'SET_STATUS_INACTIVE',
      earningsPayType: payType,
    });
  };

  return (
    <>
      <EnableModal
        componentName={salaryComponent.name}
        isOpen={isOpen && currentActiveModal === ModalActions.enable}
        isLoading={isLoading}
        onDismiss={closeModal}
        onSubmit={onSubmit}>
        {enableModalContent}
      </EnableModal>
      <DisableModal
        componentName={salaryComponent.name}
        isOpen={isOpen && currentActiveModal === ModalActions.disable}
        isLoading={isLoading}
        onDismiss={closeModal}
        onSubmit={onSubmit}>
        {disableModalContent}
      </DisableModal>
      {/*Temporarily commenting it out till we solutionise salary component deletion*/}
      {/* <DeleteModal
        componentName={componentName}
        isOpen={isOpen && currentActiveModal === ModalActions.delete}
        isLoading={isLoading}
        onDismiss={closeModal}
        onSubmit={onSubmit}
      /> */}
    </>
  );
};

export default ActionModals;
